import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobAlertsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft,
  Bell,
  Plus,
  X,
  EnvelopeSimple,
  ChatCircle,
  DeviceMobile,
  PaperPlaneTilt,
  SpinnerGap,
  CheckCircle,
  Wrench,
  MapPin,
  CurrencyDollar,
} from '@phosphor-icons/react';

const JOB_TYPES = [
  { v: 'full_time', label: 'Full-time' },
  { v: 'part_time', label: 'Part-time' },
  { v: 'contract', label: 'Contract' },
];

const FREQUENCIES = [
  { v: 'instant', label: 'Instant', sub: 'Notify the moment a match posts' },
  { v: 'daily', label: 'Daily digest', sub: 'One summary per day' },
  { v: 'weekly', label: 'Weekly digest', sub: 'One summary per week' },
];

function ChipList({ items, onRemove, color = 'blue', testidPrefix }) {
  if (!items.length) return null;
  const tone = color === 'green'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : 'bg-blue-100 text-blue-800 border-blue-300';
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, i) => (
        <span
          key={`${item}-${i}`}
          className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-bold rounded-full border-2 ${tone}`}
          data-testid={`${testidPrefix}-chip-${i}`}
        >
          {item}
          <button
            onClick={() => onRemove(i)}
            className="hover:bg-black/10 rounded-full"
            aria-label={`Remove ${item}`}
            data-testid={`${testidPrefix}-remove-${i}`}
          >
            <X size={14} weight="bold" />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function JobAlerts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [jobTypes, setJobTypes] = useState([]);
  const [frequency, setFrequency] = useState('instant');
  const [channels, setChannels] = useState({ email: true, sms: false, push: true });
  const [phone, setPhone] = useState('');

  const userType = (typeof window !== 'undefined' ? localStorage.getItem('userType') : null);
  useEffect(() => {
    if (userType !== 'worker') navigate('/');
  }, [userType, navigate]);

  const loadPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await jobAlertsAPI.getPreferences();
      const p = r.data?.preferences || {};
      setEnabled(!!p.enabled);
      setSkills(Array.isArray(p.skills) ? p.skills : []);
      setLocations(Array.isArray(p.locations) ? p.locations : []);
      setMinBudget(p.min_budget != null ? String(p.min_budget) : '');
      setMaxBudget(p.max_budget != null ? String(p.max_budget) : '');
      setJobTypes(Array.isArray(p.job_types) ? p.job_types : []);
      setFrequency(p.frequency || 'instant');
      setChannels(p.channels || { email: true, sms: false, push: true });
      setPhone(p.phone || '');
    } catch (e) {
      toast({ title: 'Failed to load preferences', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const addSkill = () => {
    const v = skillInput.trim();
    if (!v) return;
    if (skills.length >= 20) return toast({ title: 'Max 20 skills', variant: 'destructive' });
    if (skills.includes(v)) return;
    setSkills([...skills, v]);
    setSkillInput('');
  };
  const addLocation = () => {
    const v = locationInput.trim();
    if (!v) return;
    if (locations.length >= 10) return toast({ title: 'Max 10 locations', variant: 'destructive' });
    if (locations.includes(v)) return;
    setLocations([...locations, v]);
    setLocationInput('');
  };

  const toggleType = (v) => {
    setJobTypes(jobTypes.includes(v) ? jobTypes.filter(t => t !== v) : [...jobTypes, v]);
  };

  const handleSave = async () => {
    if (enabled && channels.sms && !phone.trim()) {
      toast({
        title: 'Phone number required',
        description: 'Add a phone number to receive SMS alerts.',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      await jobAlertsAPI.savePreferences({
        enabled,
        skills,
        locations,
        min_budget: minBudget ? Number(minBudget) : null,
        max_budget: maxBudget ? Number(maxBudget) : null,
        job_types: jobTypes,
        frequency,
        channels,
        phone: phone.trim(),
      });
      toast({ title: 'Saved', description: 'Your job alert preferences are active.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await jobAlertsAPI.sendTestAlert();
      const results = r.data?.results || {};
      const msgs = Object.entries(results)
        .filter(([, v]) => v != null)
        .map(([k, v]) => `${k.toUpperCase()}: ${v}`)
        .join(' · ');
      toast({ title: 'Test sent', description: msgs || 'Nothing to send (no channels enabled).' });
    } catch (e) {
      toast({ title: 'Test failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center text-neutral-600">
        <SpinnerGap size={28} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="job-alerts-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-6 border-b-4 border-black">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white" data-testid="back-btn">
              <ArrowLeft size={26} weight="bold" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
                <Bell size={28} weight="fill" className="text-amber-400" />
                Job Alerts
              </h1>
              <p className="text-neutral-300 text-sm mt-1">Get pinged the moment a job matches your filters.</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-neutral-800 border-2 border-neutral-700 rounded-full px-3 py-1.5">
            <span className={`w-2 h-2 rounded-full ${enabled ? 'bg-emerald-400' : 'bg-neutral-500'}`} />
            <span className="text-xs font-black uppercase tracking-wider">
              {enabled ? 'Active' : 'Paused'}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Master toggle */}
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-black">Alerts {enabled ? 'On' : 'Off'}</h2>
            <p className="text-sm text-neutral-500 mt-0.5">When on, we'll send you matching jobs through the channels you pick below.</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="alerts-master-toggle"
          />
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] space-y-5">
          <h2 className="text-base font-black uppercase tracking-wider text-neutral-700 border-b-2 border-neutral-200 pb-2">Filters</h2>

          {/* Skills */}
          <div>
            <Label className="font-bold mb-2 flex items-center gap-2"><Wrench size={16} weight="bold" /> Skills</Label>
            <div className="flex gap-2 mb-3">
              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                placeholder="e.g. drywall, framing, electrical"
                className="border-2 border-black rounded-lg flex-1"
                data-testid="skill-input"
              />
              <button
                onClick={addSkill}
                className="h-10 px-4 bg-black text-white font-bold rounded-lg flex items-center gap-1"
                data-testid="add-skill-btn"
              >
                <Plus size={16} weight="bold" /> Add
              </button>
            </div>
            <ChipList
              items={skills}
              onRemove={(i) => setSkills(skills.filter((_, idx) => idx !== i))}
              color="blue"
              testidPrefix="skill"
            />
          </div>

          {/* Locations */}
          <div>
            <Label className="font-bold mb-2 flex items-center gap-2"><MapPin size={16} weight="bold" /> Locations</Label>
            <div className="flex gap-2 mb-3">
              <Input
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                placeholder="e.g. Austin, TX or 78701"
                className="border-2 border-black rounded-lg flex-1"
                data-testid="location-input"
              />
              <button
                onClick={addLocation}
                className="h-10 px-4 bg-black text-white font-bold rounded-lg flex items-center gap-1"
                data-testid="add-location-btn"
              >
                <Plus size={16} weight="bold" /> Add
              </button>
            </div>
            <ChipList
              items={locations}
              onRemove={(i) => setLocations(locations.filter((_, idx) => idx !== i))}
              color="green"
              testidPrefix="location"
            />
          </div>

          {/* Budget */}
          <div>
            <Label className="font-bold mb-2 flex items-center gap-2"><CurrencyDollar size={16} weight="bold" /> Budget range</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={minBudget}
                  onChange={(e) => setMinBudget(e.target.value)}
                  placeholder="Min $"
                  className="border-2 border-black rounded-lg"
                  data-testid="min-budget-input"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Skip jobs whose max is below this.</p>
              </div>
              <div>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={maxBudget}
                  onChange={(e) => setMaxBudget(e.target.value)}
                  placeholder="Max $"
                  className="border-2 border-black rounded-lg"
                  data-testid="max-budget-input"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Skip jobs whose min is above this.</p>
              </div>
            </div>
          </div>

          {/* Job types */}
          <div>
            <Label className="font-bold mb-2 block">Job type</Label>
            <div className="flex flex-wrap gap-2">
              {JOB_TYPES.map(t => (
                <button
                  key={t.v}
                  onClick={() => toggleType(t.v)}
                  className={`h-10 px-4 text-sm font-bold rounded-lg border-2 transition-all ${jobTypes.includes(t.v) ? 'bg-amber-100 text-amber-900 border-amber-500' : 'bg-white text-neutral-700 border-neutral-300 hover:border-black'}`}
                  data-testid={`job-type-${t.v}`}
                >
                  {jobTypes.includes(t.v) && <CheckCircle size={14} weight="fill" className="inline mr-1" />}
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500 mt-2">Leave blank to match all job types.</p>
          </div>
        </div>

        {/* Channels */}
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] space-y-3">
          <h2 className="text-base font-black uppercase tracking-wider text-neutral-700 border-b-2 border-neutral-200 pb-2">Notification channels</h2>
          {[
            { key: 'email', label: 'Email', icon: <EnvelopeSimple size={20} weight="bold" />, sub: "Detailed job card with apply button" },
            { key: 'sms', label: 'SMS text', icon: <ChatCircle size={20} weight="bold" />, sub: 'Short text with title, location, and budget' },
            { key: 'push', label: 'Push (in-app)', icon: <DeviceMobile size={20} weight="bold" />, sub: 'Real-time browser/PWA notification' },
          ].map(ch => (
            <label
              key={ch.key}
              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${channels[ch.key] ? 'border-black bg-amber-50' : 'border-neutral-200 hover:border-neutral-400'}`}
              data-testid={`channel-${ch.key}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 ${channels[ch.key] ? 'bg-amber-500 text-black border-black' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
                {ch.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-neutral-900">{ch.label}</p>
                <p className="text-xs text-neutral-500">{ch.sub}</p>
              </div>
              <Switch
                checked={!!channels[ch.key]}
                onCheckedChange={(v) => setChannels({ ...channels, [ch.key]: v })}
                data-testid={`channel-${ch.key}-switch`}
              />
            </label>
          ))}

          {channels.sms && (
            <div className="pt-2" data-testid="phone-section">
              <Label className="font-bold mb-2 block">Phone for SMS</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="border-2 border-black rounded-lg"
                data-testid="phone-input"
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                Include country code. Standard message rates may apply. Reply STOP to opt out.
              </p>
            </div>
          )}
        </div>

        {/* Frequency */}
        <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] space-y-3">
          <h2 className="text-base font-black uppercase tracking-wider text-neutral-700 border-b-2 border-neutral-200 pb-2">Frequency</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {FREQUENCIES.map(f => (
              <button
                key={f.v}
                onClick={() => setFrequency(f.v)}
                className={`p-3 border-2 rounded-lg text-left transition-all ${frequency === f.v ? 'border-black bg-amber-50' : 'border-neutral-200 hover:border-neutral-400'}`}
                data-testid={`freq-${f.v}`}
              >
                <p className="font-bold text-neutral-900">{f.label}</p>
                <p className="text-[11px] text-neutral-500 mt-1">{f.sub}</p>
              </button>
            ))}
          </div>
          {frequency !== 'instant' && (
            <p className="text-[11px] text-amber-700 italic">Note: digest mode is queued — instant delivery is currently the only fully-active mode.</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 sticky bottom-0 -mx-4 px-4 py-3 bg-[#F0F0F2] border-t-2 border-black sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent sm:border-0">
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex-1 h-12 flex items-center justify-center gap-2 bg-white text-neutral-900 font-bold rounded-xl border-2 border-black hover:bg-neutral-50 disabled:opacity-50"
            data-testid="send-test-btn"
          >
            {testing ? <SpinnerGap size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="bold" />}
            Send test
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-[2] h-12 flex items-center justify-center gap-2 bg-amber-500 text-black font-black rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
            data-testid="save-prefs-btn"
          >
            {saving ? <SpinnerGap size={18} className="animate-spin" /> : <CheckCircle size={18} weight="bold" />}
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
